import { sortableKeyboardCoordinates } from '@repo/ui/dnd'

import type { TableColumnPlacement, TableColumnRegion } from './context/table-layout.ts'
import type { TableColumnPinningState } from './tanstack-table'

export const TABLE_SETTINGS_DND_ACTIVATION = {
  pointer: { distance: 8 },
  touch: { delay: 180, tolerance: 8 },
} as const

export const TABLE_SETTINGS_KEYBOARD_COORDINATE_GETTER = sortableKeyboardCoordinates

export const shouldActivateTableSettingsPointer = (
  event: Pick<PointerEvent, 'button' | 'isPrimary' | 'pointerType'>,
) => event.pointerType !== 'touch' && event.isPrimary && event.button === 0

export const getTableSettingsDropPlacement = ({
  activeRect,
  deltaY,
  overRect,
}: {
  activeRect?: null | { height: number; top: number }
  deltaY: number
  overRect?: null | { height: number; top: number }
}): TableColumnPlacement | undefined => {
  if (activeRect && overRect) {
    const activeCenter = activeRect.top + activeRect.height / 2
    const overCenter = overRect.top + overRect.height / 2
    if (activeCenter !== overCenter) return activeCenter > overCenter ? 'after' : 'before'
  }

  if (deltaY === 0) return undefined
  return deltaY > 0 ? 'after' : 'before'
}

/** What table settings and header drag call each region. */
export const TABLE_COLUMN_REGION_LABELS = {
  center: 'Unpinned',
  end: 'Pinned right',
  start: 'Pinned left',
} as const satisfies Record<TableColumnRegion, string>

export const getTableSettingsDragEndAnnouncement = () => undefined

export const getTableSettingsNoMoveAnnouncement = (label: string) => `${label} was not moved.`

export const getTableSettingsMoveAnnouncement = ({
  destination,
  label,
  position,
  total,
}: {
  destination: string
  label: string
  position: number
  total: number
}) => `${label} moved to ${destination}, position ${position} of ${total}.`

export const getTableSettingsPosition = ({
  id,
  layout,
  leafColumnIds,
  region,
}: {
  id: string
  layout: { columnOrder: string[]; pinning: TableColumnPinningState }
  leafColumnIds: string[]
  region: 'center' | 'end' | 'start'
}) => {
  const leafIds = new Set(leafColumnIds)
  const ids =
    region === 'center'
      ? layout.columnOrder.filter(
          columnId =>
            leafIds.has(columnId) &&
            !layout.pinning.start?.includes(columnId) &&
            !layout.pinning.end?.includes(columnId),
        )
      : (layout.pinning[region] ?? []).filter(columnId => leafIds.has(columnId))
  const position = ids.indexOf(id)

  return { position: position >= 0 ? position + 1 : ids.length, total: ids.length }
}
